import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Search, Loader2, User } from 'lucide-react';
import { useToast } from '@/components/ui/use-toast';
import { getEmployeeByCedula, saveClockInRecord, fetchWorkedHours } from '../services/workedHours.services';
import { useAuth } from '@/contexts/AuthContext';
import PlanificacionLlegada, { useVerificacionLlegada, registroBloqueado } from './PlanificacionLlegada';
import FotoLlegada from './FotoLlegada';

const ClockInDialog = ({ idLavanderia, onClockIn }) => {
  const [open, setOpen] = useState(false);
  const [cedula, setCedula] = useState('');
  const [foundEmployee, setFoundEmployee] = useState(null);
  const [openRecord, setOpenRecord] = useState(null);
  const [isSearching, setIsSearching] = useState(false);
  const [imgSrc, setImgSrc] = useState(null);
  const [calculatedTime, setCalculatedTime] = useState('');
  const [error, setError] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const { toast } = useToast();
  const { user } = useAuth();
  // Restricción de llegada del centro: verificación contra la planificación de turnos
  const { verificacion, cargando: verificando, error: errorVerificacion } = useVerificacionLlegada(idLavanderia, foundEmployee);
  const [excepcion, setExcepcion] = useState({ activa: false, motivo: '' });
  const puedeExcepcion = ['admin', 'supervisor'].includes(String(user?.role || '').toLowerCase());
  const bloqueado = registroBloqueado(verificacion, excepcion);

  const resetForm = () => {
    setCedula('');
    setFoundEmployee(null);
    setOpenRecord(null);
    setImgSrc(null);
    setError('');
    setIsSearching(false);
    setIsSaving(false);
    setExcepcion({ activa: false, motivo: '' });
  };

  const formatTime = (date) => date.toTimeString().slice(0, 5);

  useEffect(() => {
    if (open) {
      resetForm();
      // Hora exacta actual (sin redondeo)
      setCalculatedTime(formatTime(new Date()));
    }
  }, [open]);

  const handleSearch = async () => {
    if (!cedula) {
      setError('Por favor, ingrese una cédula.');
      return;
    }
    setIsSearching(true);
    setError('');
    setFoundEmployee(null);
    setOpenRecord(null);
    setExcepcion({ activa: false, motivo: '' });
    try {
      const response = await getEmployeeByCedula(cedula);
      if (response.state) {
        const employee = response.data;

        // Verificar que no tenga un registro de entrada sin hora de salida
        const pending = await fetchWorkedHours({
          limit: 1,
          page: 1,
          query: { 'empleado.id': employee._id, horaSalida: null },
        });

        if (pending?.state && pending.data?.length > 0) {
          setOpenRecord(pending.data[0]);
        } else {
          setFoundEmployee(employee);
        }
      } else {
        setError(response.message || 'Empleado no encontrado.');
        toast({
          title: 'Error de Búsqueda',
          description: response.message || 'No se pudo encontrar al empleado con esa cédula.',
          variant: 'destructive',
        });
      }
    } catch (err) {
      setError('Error al buscar el empleado.');
      console.error('Error searching employee:', err);
    } finally {
      setIsSearching(false);
    }
  };

  const handleRetake = () => {
    setImgSrc(null);
  };

  const handleSubmit = async () => {
    if (!foundEmployee || !imgSrc) {
      setError('Por favor, busque un empleado y tome una foto.');
      return;
    }

    const laundryData = JSON.parse(localStorage.getItem('laundry'));

    const nowHHmm = formatTime(new Date()); // asegurar hora exacta al momento de guardar
    const record = {
      fecha: new Date(),
      horaIngreso: nowHHmm,
      lavanderia: {
        id: idLavanderia,
        nombre: laundryData?.nombre || 'N/A',
      },
      empleado: {
        id: foundEmployee._id,
        nombre: foundEmployee.nombre + ' ' + foundEmployee.apellido,
        cedula: foundEmployee.cedula,
        rol: foundEmployee?.rol,
      },
      salario: foundEmployee.salario,
      horaSalida: null,
      totalHorasTrabajadas: null,
      isAprobado: false,
      foto: imgSrc, // Base64 de la imagen
    };
    // En bloqueo, sin planificación, solo se registra por excepción con motivo
    if (verificacion?.modo === 'bloqueo' && !verificacion.planificado && excepcion.activa) {
      record.excepcion = {
        motivo: excepcion.motivo.trim(),
        autorizadoPor: { id: user?._id || user?.id || undefined, nombre: user?.nombre || user?.name || user?.email || undefined },
      };
    }

    setIsSaving(true);
    setError('');

    try {
      await saveClockInRecord(record);
      toast({
        title: 'Éxito',
        description: 'Se ha registrado la llegada del empleado.',
      });
      if (onClockIn) onClockIn();
      setOpen(false);
    } catch (err) {
      const mensaje = err?.message || 'No se pudo guardar el registro de llegada.';
      setError(mensaje);
      toast({
        title: 'No se registró la llegada',
        description: mensaje,
        variant: 'destructive',
      });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button>Registrar Llegada</Button>
      </DialogTrigger>
      <DialogContent className="w-[calc(100vw-2rem)] sm:max-w-4xl max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Registrar Hora de Llegada</DialogTitle>
          <DialogDescription>Busca al empleado por cédula, revisa su planificación y toma la foto de evidencia.</DialogDescription>
        </DialogHeader>

        <div className="grid gap-5 md:grid-cols-2">
          {/* Izquierda: empleado y planificación */}
          <div className="space-y-4 min-w-0">
            <div className="space-y-1.5">
              <Label htmlFor="cedula">Cédula del empleado</Label>
              <div className="flex items-center gap-2">
                <Input
                  id="cedula"
                  className="flex-1"
                  value={cedula}
                  onChange={(e) => setCedula(e.target.value)}
                  placeholder="Ingrese la cédula del empleado"
                  onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                  autoFocus
                />
                <Button onClick={handleSearch} disabled={isSearching} size="icon" variant="outline" title="Buscar">
                  {isSearching ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
                </Button>
              </div>
            </div>

            {foundEmployee && (
              <div className="flex items-start gap-3 rounded-md border-l-4 border-primary bg-primary/10 p-3">
                <User className="h-5 w-5 mt-0.5 shrink-0 text-primary" />
                <div className="min-w-0">
                  <p className="font-semibold truncate">{foundEmployee.nombre + ' ' + foundEmployee.apellido}</p>
                  <p className="text-sm text-muted-foreground">
                    Cédula {foundEmployee.cedula}{foundEmployee.rol ? ` · ${foundEmployee.rol}` : ''}
                  </p>
                  <p className="text-sm text-muted-foreground">Lavandería: {foundEmployee.lavanderia?.nombre || 'N/A'}</p>
                </div>
              </div>
            )}

            {foundEmployee && (
              <PlanificacionLlegada
                verificacion={verificacion}
                cargando={verificando}
                error={errorVerificacion}
                permiteExcepcion={puedeExcepcion}
                excepcion={excepcion}
                onExcepcionChange={setExcepcion}
              />
            )}

            {openRecord && (
              <div className="rounded-md border-l-4 border-amber-500 bg-amber-50 dark:bg-amber-950 p-3">
                <p className="font-semibold text-amber-800 dark:text-amber-200">Entrada pendiente por cerrar</p>
                <p className="text-sm text-amber-700 dark:text-amber-300">
                  Ingreso registrado a las {openRecord.horaIngreso} el {new Date(openRecord.fecha).toLocaleDateString()}. Debe registrar la salida antes de marcar un nuevo ingreso.
                </p>
              </div>
            )}

            {!foundEmployee && !openRecord && (
              <p className="text-sm text-muted-foreground">Ingresa la cédula y presiona Enter para buscar al empleado.</p>
            )}

            {error && <p className="text-sm text-red-600 dark:text-red-400">{error}</p>}
          </div>

          {/* Derecha: foto de evidencia */}
          <div className="space-y-1.5 min-w-0">
            <Label>Evidencia fotográfica</Label>
            <FotoLlegada imgSrc={imgSrc} onCapture={setImgSrc} onRetake={handleRetake} hora={calculatedTime} disabled={isSaving} />
          </div>
        </div>

        <DialogFooter className="gap-2">
          <Button type="button" variant="secondary" onClick={() => setOpen(false)}>Cancelar</Button>
          <Button type="submit" onClick={handleSubmit} disabled={!foundEmployee || !imgSrc || isSaving || verificando || bloqueado}>
            {isSaving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
            Registrar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ClockInDialog;
